import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, extractErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, ShieldCheck, FileText, Search, Send, CheckCircle2, Loader2 } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const SERVICE_ICONS = [FileText, Search, ShieldCheck];
const EMPTY = { name: "", email: "", phone: "", firm_name: "", service_type: "", case_number: "", defendant: "", service_address: "", message: "" };

export default function AgencySite() {
  const { slug: slugParam } = useParams();
  const [slug, setSlug] = useState(slugParam || null);
  const [agency, setAgency] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const req = slugParam
      ? api.get(`/agencies/${slugParam}/public`)
      : api.get(`/agencies/resolve?host=${window.location.hostname}`);
    req.then(({ data }) => { setAgency(data); if (!slugParam && data?.slug) setSlug(data.slug); })
       .catch(() => setNotFound(true));
  }, [slugParam]);

  useSeo({
    title: agency ? `${agency.name} | Professional Process Serving` : "Process Serving",
    description: agency?.tagline || "Professional process serving and legal support services.",
    canonicalPath: `/a/${slug}`,
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required."); return; }
    setSubmitting(true);
    try {
      await api.post(`/agencies/${slug}/intake`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Request submitted. The agency will contact you shortly.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not submit your request. Please try again."));
    } finally { setSubmitting(false); }
  };

  if (notFound) {
    return (
      <div data-testid="agency-not-found" className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Agency not found</h1>
          <p className="mt-2 text-slate-600">This agency page is not available.</p>
        </div>
      </div>
    );
  }
  if (!agency) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  const accent = agency.primary_color || "#0f766e";

  return (
    <div data-testid="agency-site" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            {agency.logo_url ? (
              <img src={agency.logo_url} alt={agency.name} className="h-10 w-auto" />
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{ backgroundColor: accent }}>
                <ShieldCheck className="h-6 w-6" />
              </span>
            )}
            <span data-testid="agency-name" className="font-display text-lg font-bold text-slate-900 sm:text-xl">{agency.name}</span>
          </div>
          <a href="#request" data-testid="agency-header-cta"
             className="inline-flex min-h-[44px] items-center rounded-xl px-5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
             style={{ backgroundColor: accent }}>
            {agency.cta_text || "Request Service"}
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-widest sm:text-sm" style={{ color: accent }}>Professional Process Serving</p>
          <h1 className="mt-3 max-w-4xl font-display text-4xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {agency.name}
          </h1>
          {agency.tagline && <p className="mt-5 max-w-3xl text-lg leading-relaxed text-slate-600 sm:text-xl">{agency.tagline}</p>}
          {agency.service_area && (
            <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-slate-700">
              <MapPin className="h-4 w-4" style={{ color: accent }} /> {agency.service_area}
            </p>
          )}
          <div className="mt-8">
            <a href="#request" data-testid="agency-hero-cta"
               className="inline-flex min-h-[48px] items-center gap-2 rounded-xl px-6 text-base font-semibold text-white shadow-lg transition hover:opacity-90"
               style={{ backgroundColor: accent }}>
              {agency.cta_text || "Request Service"} <Send className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* About */}
      {agency.about && (
        <section className="px-5 py-14 sm:px-8 lg:py-20">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">About Us</h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">{agency.about}</p>
          </div>
        </section>
      )}

      {/* Services */}
      {agency.services?.length > 0 && (
        <section className="bg-white px-5 py-14 sm:px-8 lg:py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Our Services</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {agency.services.map((s, i) => {
                const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
                return (
                  <div key={i} data-testid={`agency-service-${i}`} className="rounded-2xl border border-slate-200 bg-white p-6 transition hover:shadow-md">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${accent}1a` }}>
                      <Icon className="h-6 w-6" style={{ color: accent }} />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                    {s.description && <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{s.description}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Contact + Intake */}
      <section id="request" className="px-5 py-14 sm:px-8 lg:py-20">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Request Service</h2>
            <p className="mt-3 text-slate-600">Tell us about your case and we'll get in touch to set up your service.</p>
            <div className="mt-6 space-y-3">
              {agency.phone && (
                <a href={`tel:${agency.phone}`} data-testid="agency-phone" className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-sm">
                  <Phone className="h-5 w-5" style={{ color: accent }} /> <span className="text-slate-700">{agency.phone}</span>
                </a>
              )}
              {agency.email && (
                <a href={`mailto:${agency.email}`} data-testid="agency-email" className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-sm">
                  <Mail className="h-5 w-5" style={{ color: accent }} /> <span className="text-slate-700">{agency.email}</span>
                </a>
              )}
              {agency.address && (
                <div data-testid="agency-address" className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0" style={{ color: accent }} /> <span className="text-slate-700">{agency.address}</span>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            {done ? (
              <div data-testid="intake-success" className="flex flex-col items-center py-10 text-center">
                <CheckCircle2 className="h-14 w-14" style={{ color: accent }} />
                <h3 className="mt-4 font-display text-xl font-bold text-slate-900">Request received</h3>
                <p className="mt-2 text-slate-600">Thank you. The agency will review your request and contact you shortly.</p>
                <Button data-testid="intake-another" variant="outline" className="mt-6" onClick={() => setDone(false)}>Submit another request</Button>
              </div>
            ) : (
              <form data-testid="agency-intake-form" onSubmit={submit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="in-name">Your name *</Label>
                    <Input id="in-name" data-testid="intake-name" value={form.name} onChange={set("name")} required />
                  </div>
                  <div>
                    <Label htmlFor="in-email">Email *</Label>
                    <Input id="in-email" data-testid="intake-email" type="email" value={form.email} onChange={set("email")} required />
                  </div>
                  <div>
                    <Label htmlFor="in-phone">Phone</Label>
                    <Input id="in-phone" data-testid="intake-phone" value={form.phone} onChange={set("phone")} />
                  </div>
                  <div>
                    <Label htmlFor="in-firm">Firm / Company</Label>
                    <Input id="in-firm" data-testid="intake-firm" value={form.firm_name} onChange={set("firm_name")} />
                  </div>
                  <div>
                    <Label htmlFor="in-service">Service type</Label>
                    <Input id="in-service" data-testid="intake-service-type" placeholder="e.g. Process serving" value={form.service_type} onChange={set("service_type")} />
                  </div>
                  <div>
                    <Label htmlFor="in-case">Case number</Label>
                    <Input id="in-case" data-testid="intake-case" value={form.case_number} onChange={set("case_number")} />
                  </div>
                  <div>
                    <Label htmlFor="in-def">Party to be served</Label>
                    <Input id="in-def" data-testid="intake-defendant" value={form.defendant} onChange={set("defendant")} />
                  </div>
                  <div>
                    <Label htmlFor="in-addr">Service address</Label>
                    <Input id="in-addr" data-testid="intake-address" value={form.service_address} onChange={set("service_address")} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="in-msg">Details</Label>
                  <Textarea id="in-msg" data-testid="intake-message" rows={4} value={form.message} onChange={set("message")} placeholder="Anything else we should know?" />
                </div>
                <Button type="submit" data-testid="intake-submit" disabled={submitting}
                        className="w-full text-white hover:opacity-90" style={{ backgroundColor: accent }}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit request <Send className="ml-2 h-4 w-4" /></>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-slate-900 px-5 py-10 text-center text-sm text-slate-400 sm:px-8">
        © {new Date().getFullYear()} {agency.name}. {agency.service_area}
      </footer>
    </div>
  );
}
